export type DateInput = string | number | Date | null | undefined;

export const isLocalhost = (): boolean => {
  if (
    globalThis.location.hostname === "localhost" ||
    globalThis.location.hostname === "127.0.0.1" ||
    globalThis.location.hostname === ""
  ) {
    return true;
  }

  return false;
};

export const classNames = (...classes: any) => {
  return classes.filter(Boolean).join(" ");
};

export const mergeErrors = (errors: any) => {
  const merged: any = {};
  for (const key in errors.params) {
    if (errors.params.hasOwnProperty(key)) {
      merged[key] = merged[key] || [];
      merged[key] = merged[key].concat(errors.params[key]);
    }
  }

  for (const key in errors.body) {
    if (errors.body.hasOwnProperty(key)) {
      merged[key] = merged[key] || [];
      merged[key] = merged[key].concat(errors.body[key]);
    }
  }

  return merged;
};

export const camelToDash = (str: string) => {
  if (str !== str.toLowerCase()) {
    str = str.replaceAll(/[A-Z]/g, m => "-" + m.toLowerCase());
  }
  return str;
};


export const camelToUnderscore = (str: string) => {
  if (str !== str.toLowerCase()) {
    str = str.replaceAll(/[A-Z]/g, m => "_" + m.toLowerCase());
  }
  return str;
};


export const createAbortController = () => {
  const abortController = new AbortController();
  const abortSignal = abortController.signal;
  return { abortController, abortSignal };
};

export const stripEmptyProperties = (obj: any) => {
  return Object.fromEntries(
    Object.entries(obj).filter(
      ([_, value]) => value !== null && value !== undefined && value !== ""
    )
  );
};

/**
 * Safely creates a Date object from a date string, avoiding timezone issues
 * @param dateString - Date string in YYYY-MM-DD format
 * @returns Date object or null if invalid
 */
export const createSafeDate = (dateString: string | null | undefined): Date | null => {
  if (!dateString || dateString.trim() === "") {
    return null;
  }
  
  // If the date string is in YYYY-MM-DD format, parse it as local date to avoid timezone issues
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    const [year, month, day] = dateString.split('-').map(Number);
    return new Date(year, month - 1, day); // month is 0-indexed in Date constructor
  }
  
  // For other formats, use the standard Date constructor
  const date = new Date(dateString);
  return Number.isNaN(date.getTime()) ? null : date;
};

/**
 * Formats a date string to MM/DD/YYYY format for display
 * @param dateString - Date string in YYYY-MM-DD format
 * @returns Formatted date string or 'N/A' if invalid
 */
export const formatDateToUS = (dateString: string | null | undefined): string => {
  const date = createSafeDate(dateString);
  if (!date) {
    return 'N/A';
  }
  
  return date.toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric'
  });
};

/**
 * Validates if a date of birth represents someone who is at least 18 years old
 * @param dateOfBirth - Date string in YYYY-MM-DD format
 * @returns boolean indicating if the person is at least 18 years old
 */
export const validateDateOfBirth = (dateOfBirth: string | null | undefined): boolean => {
  const birthDate = createSafeDate(dateOfBirth);
  if (!birthDate) {
    return false;
  }
  
  const today = new Date();
  const age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  
  // Check if birthday has occurred this year
  const hasHadBirthday = monthDiff > 0 || (monthDiff === 0 && today.getDate() >= birthDate.getDate());
  const actualAge = hasHadBirthday ? age : age - 1;
  
  return actualAge >= 18 && birthDate <= today;
};

/**
 * Safely creates a Date object from various date formats, avoiding timezone issues
 * @param dateInput - Date string, timestamp, or Date object
 * @returns Date object or null if invalid
 */
export const createSafeDateFromAny = (dateInput: DateInput): Date | null => {
  if (!dateInput) {
    return null;
  }
  
  // If it's already a Date object
  if (dateInput instanceof Date) {
    return Number.isNaN(dateInput.getTime()) ? null : dateInput;
  }
  
  // If it's a timestamp (number)
  if (typeof dateInput === 'number') {
    const date = new Date(dateInput);
    return Number.isNaN(date.getTime()) ? null : date;
  }
  
  // If it's a string
  if (typeof dateInput === 'string') {
    // Handle YYYY-MM-DD format
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateInput)) {
      const [year, month, day] = dateInput.split('-').map(Number);
      return new Date(year, month - 1, day);
    }
    
    // Handle ISO strings with time components - extract just the date part
    if (/^\d{4}-\d{2}-\d{2}T/.test(dateInput)) {
      const datePart = dateInput.split('T')[0];
      const [year, month, day] = datePart.split('-').map(Number);
      return new Date(year, month - 1, day);
    }
    
    // Handle other formats
    const date = new Date(dateInput);
    return Number.isNaN(date.getTime()) ? null : date;
  }
  
  return null;
};

/**
 * Formats any date input to MM/DD/YYYY format for display
 * @param dateInput - Date string, timestamp, or Date object
 * @returns Formatted date string or 'N/A' if invalid
 */
export const formatAnyDateToUS = (dateInput: DateInput): string => {
  const date = createSafeDateFromAny(dateInput);
  if (!date) {
    return 'N/A';
  }
  
  return date.toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric'
  });
};

/**
 * Formats any date input to a long format (e.g., "January 15, 2024")
 * @param dateInput - Date string, timestamp, or Date object
 * @returns Formatted date string or 'N/A' if invalid
 */
export const formatAnyDateToLong = (dateInput: DateInput): string => {
  const date = createSafeDateFromAny(dateInput);
  if (!date) {
    return 'N/A';
  }
  
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

/**
 * Formats any date input to time string
 * @param dateInput - Date string, timestamp, or Date object
 * @returns Formatted time string or 'N/A' if invalid
 */
export const formatAnyDateToTime = (dateInput: DateInput): string => {
  const date = createSafeDateFromAny(dateInput);
  if (!date) {
    return 'N/A';
  }
  
  return date.toLocaleTimeString('en-US');
};